import React, { useState } from 'react'
import { useTranslation } from 'next-i18next'
import { EyeOutlined } from '@ant-design/icons'
import { Button, Row, Switch, Tooltip } from 'antd'
import { CustomText, CustomCol } from 'src/components'
import ProjectPreviewModal from './ProjectPreviewModal/ProjectPreviewModal'
import { useAppDispatch, useAppSelector, useWindowSize } from 'src/app/hooks'
import {
  isSubmitProjectDisabledSelector,
  projectApiFilesPayloadSelector,
  createBigProjectStateSelector,
  newProjectImagesFilesSelector,
  projectFormIsActiveSelector,
  editBigProjectStateSelector,
  projectImagesFilesSelector,
  projectApiPayloadSelector,
  projectFormStateSelector,
  isActiveDisabledSelector,
  setProjectFormItemValue,
  projectFormKindSelector,
  projectDetailsSelector,
  createBigProject,
  editBigProject,
} from 'src/features'

const ProjectFormFooter: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const isActive = useAppSelector(projectFormIsActiveSelector)
  const isActiveDisabled = useAppSelector(isActiveDisabledSelector)
  const projectKindSelected = useAppSelector(projectFormKindSelector)
  const isSubmitProjectDisabled = useAppSelector(isSubmitProjectDisabledSelector)
  const projectPayload = useAppSelector(projectApiPayloadSelector)
  const projectFilesPayload = useAppSelector(projectApiFilesPayloadSelector)
  const projectImagesPayload = useAppSelector(projectImagesFilesSelector)
  const newProjectImagesPyload = useAppSelector(newProjectImagesFilesSelector)
  const { discounts } = useAppSelector(projectFormStateSelector)
  const { loading: loadingCreate } = useAppSelector(createBigProjectStateSelector)
  const { loading: loadingEdit } = useAppSelector(editBigProjectStateSelector)
  const { project: projectToEdit } = useAppSelector(projectDetailsSelector)

  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false)
  const { isMobile } = useWindowSize()

  const handleProjectSubmit = async () => {
    const files = await projectFilesPayload
    const projectImages = await projectImagesPayload
    const newImages = await newProjectImagesPyload
    if (!projectToEdit) {
      dispatch(createBigProject(projectPayload, files, projectImages, discounts))
    } else {
      dispatch(editBigProject(projectToEdit, projectPayload, files, newImages, discounts))
    }
  }

  const submitLabel = {
    EDIT: { submit: t('Projects.EditProject'), submiting: t('Projects.EditingProject') },
    ADD: { submit: t('Projects.SubmitProject'), submiting: t('Projects.CreatingProject') }
  }[projectToEdit ? 'EDIT' : 'ADD']


  const switchContent = <Switch
    size='small'
    checked={isActive}
    onChange={(value) => dispatch(setProjectFormItemValue({ key: 'is_active', value }))}
    disabled={isActiveDisabled}
  />

  return (
    <>
      <Row className='mt-4'>
        <CustomCol className='mt-2' lg={12}>
          {isActiveDisabled ? <Tooltip title={t('Projects.ActiveError')} color={'red'}>
            {switchContent}
          </Tooltip> : <>{switchContent}</>}
          <CustomText
            label={isActive ? t('Projects.ProjectActive') : t('Projects.ProjectDesactivated')}
            className='ml-2'
            strong
          />
        </CustomCol>
        <CustomCol lg={7} className='float-right'>
          <Button
            onClick={() => setIsPreviewModalVisible(true)}
            icon={<EyeOutlined />}
            className={isMobile ? 'radius-100 mt-4' : 'radius-100 float-right'}
            disabled={!projectKindSelected}
            size='large'
            block={isMobile}
          >
            {t('Projects.Preview')}
          </Button>
        </CustomCol>
        <CustomCol lg={5}>
          <Button
            type='primary'
            size='large'
            className={isMobile ? 'radius-100 mt-4' : 'radius-100 float-right'}
            disabled={isSubmitProjectDisabled}
            block={isMobile}
            loading={(loadingCreate || loadingEdit)}
            onClick={handleProjectSubmit}
          >
            {(loadingCreate || loadingEdit) ? submitLabel.submiting : submitLabel.submit}
          </Button>
        </CustomCol>
      </Row>

      <ProjectPreviewModal
        isModalVisible={isPreviewModalVisible}
        handleClose={() => setIsPreviewModalVisible(false)}
      />
    </>
  )
}

export default ProjectFormFooter
